import { urls } from '@/config/assets';
import { three } from '@/tunnels';
import { Box } from '@react-three/drei';
import { ECS } from './state';
import {
  OrthographicCamera,
  PerspectiveCamera,
  Grid,
  Hud,
} from '@react-three/drei';
import { getUrlBoolean, getUrlInt } from '@/helpers/UrlParam';
import {
  EffectComposer,
  DepthOfField,
  Bloom,
  Noise,
  Vignette,
  Pixelation,
} from '@react-three/postprocessing';
import { forwardRef, useEffect } from 'react';
import { useAppStore } from '@/stores/app';
import { useGameStore } from '@/stores/game';
import { resetSpawnSystem } from './systems/SystemSpawn';
import {
  players,
  collectibles,
  obstacles,
  clouds,
  characters,
  endings,
  crashed,
} from './queries';
import { cn } from '@/lib/utils';
import AssetService from '@/services/AssetService';

const debug = getUrlBoolean('debug');

// Function to reset all game entities
const resetGameEntities = () => {
  // console.log('BeerGame.resetGameEntities');

  // Remove crashed
  // for (const item of crashed) {
  //   ECS.world.removeComponent(item, 'isCrashed');
  // }

  // Remove all collectibles
  for (const collectible of collectibles) {
    ECS.world.remove(collectible);
  }

  // Remove all obstacles
  for (const obstacle of obstacles) {
    ECS.world.remove(obstacle);
  }

  // Remove all clouds
  for (const cloud of clouds) {
    ECS.world.remove(cloud);
  }

  // Remove all characters
  for (const character of characters) {
    ECS.world.remove(character);
  }

  // Remove all endings
  for (const ending of endings) {
    ECS.world.remove(ending);
  }

  // Reset player position and state
  for (const player of players) {
    player.position.x = 0;
    player.position.y = 0;
    player.position.z = 0;
    player.velocity.x = 0;
    player.velocity.y = 0;
    player.velocity.z = 0;
    // player.isJumping = false;
    // player.isBlocked = false;
    ECS.world.removeComponent(player, 'isJumping');
    ECS.world.removeComponent(player, 'isBlocked');
    ECS.world.addComponent(player, 'stop', true);
  }
};

const stopGame = () => {
  // console.log('BeerGame.stopGame');
  for (const player of players) {
    ECS.world.addComponent(player, 'stop', true);
  }
};

export const BeerGame = forwardRef(({}, ref) => {
  const page = useAppStore((state) => state.page);
  const show = page == 'game';
  const setPopup = useAppStore((state) => state.setPopup);
  const { t } = useTranslation();
  const hasCrashed = useGameStore((state) => state.hasCrashed);
  const setHasCrashed = useGameStore((state) => state.setHasCrashed);

  const refEnding = useRef(null);
  const [stage, setStage] = useState(0);

  // Function to restart the game
  const resetGame = useCallback(() => {
    // console.log('BeerGame.resetGame');
    resetSpawnSystem();
    resetGameEntities();
    resetEnding();
    setHasCrashed(false);
  }, []);

  const restartGame = useCallback(() => {
    resetGame();
    setStage(getUrlInt('stage', 0));
  }, [setStage]);

  const onStageEnded = useCallback(() => {
    setPopup(`end${stage}`);
    AssetService.getAsset('s_music').stop();
    const stageSfx = AssetService.getAsset(`s_level_end_${stage}`);
    stageSfx.stop();
    stageSfx.play();
  }, [setPopup, stage]);

  const onEndingEnded = useCallback(() => {
    setPopup('ending');
  }, [setPopup]);

  const onGameFinished = () => {
    AssetService.getAsset('s_music').stop();
    refEnding.current.style.display = 'block';
    refEnding.current.play();
  };

  const toNextStage = useCallback(() => {
    // console.log('BeerGame.toNextStage', stage);
    let nextStage = stage + 1;
    setStage(nextStage);
    resetGame();
    setPopup(`pre${nextStage}0`);
  }, [setStage, stage, setPopup]);

  const playMusic = () => {
    let music = AssetService.getAsset('s_music');
    music.volume = 0.25;
    music.loop = true;
    music.stop();
    music.play();
  };

  const startStage = () => {
    for (const player of players) {
      ECS.world.removeComponent(player, 'stop');
    }
    playMusic();
  };

  useEffect(() => {
    // console.log('BeerGame.useEffect', show);
    if (show) {
      restartGame();
      playMusic();

      setPopup(`pre00`);
    }
  }, [show]);

  // CRASHED
  // const hasCrashed = useCrashed();
  useEffect(() => {
    if (hasCrashed) {
      // setPopup(stage == 2 ? 'dead2' : 'dead');
      setPopup('dead');
      stopGame();
    }
  }, [hasCrashed, stage, setPopup]);

  const onStopClose = useCallback(() => {
    // console.log('onStopClose');
    resetGame();
    startStage();
  }, [resetGame]);

  return (
    <section className={cn(['page game game-beer'])}>
      <three.In>
        <group>
          <BackgroundColor color={0x72beff} />
          <BeerCamera />

          <ambientLight intensity={1.5} />
          <directionalLight
            intensity={2}
            position={[3, 3, 3]}
          />

          {debug && (
            <Grid
              args={[1000, 1000]}
              cellSize={10}
              cellThickness={1}
              sectionSize={1}
              infiniteGrid={true}
            />
          )}

          <Collectibles />
          <Obstacles />
          <BeerGround stage={stage} />
          <Characters />
          <Clouds />
          <Player />
          <Endings />

          <SystemControls stage={stage} />
          <SystemEnding
            stage={stage}
            onEnded={onStageEnded}
          />
          <SystemPhysics />
          <SystemSpawn stage={stage} />

          <SystemCollision />
          <SystemRender />
          <SystemOnScreen />
        </group>

        <PreCompile />

        <EffectComposer renderPriority={2}>
          <Pixelation granularity={0} />
        </EffectComposer>

        <Hud renderPriority={3}>
          <BeerScore stage={stage} />
        </Hud>
      </three.In>

      {debug && (
        <div
          className={cn('stage', 'fixed top-3 right-3 text-[2rem] font-bold')}
        >
          Stage: {stage}
          <br />
          Crashed: {hasCrashed ? 'true' : 'false'}
        </div>
      )}

      <ButtonMute />

      <PopupDead
        onClose={onStopClose}
        stage={stage}
      />

      {/* PRE */}
      <PopupMessage
        popupId="pre00"
        onClose={() => {
          setPopup('pre01');
        }}
      >
        <div className="whitespace-pr">{t('game.stages.0.pre.0')}</div>
      </PopupMessage>
      <PopupMessage
        popupId="pre01"
        onClose={startStage}
      >
        <div className="whitespace-pre">{t('game.stages.0.pre.1')}</div>
      </PopupMessage>
      <PopupMessage
        popupId="pre10"
        onClose={startStage}
      >
        <div className="whitespace-pr">{t('game.stages.1.pre.0')}</div>
      </PopupMessage>
      <PopupMessage
        popupId="pre20"
        onClose={startStage}
      >
        <div className="whitespace-pr">{t('game.stages.2.pre.0')}</div>
      </PopupMessage>

      {/* END */}
      <PopupMessage
        popupId="end0"
        onClose={toNextStage}
      >
        <div className="whitespace-pr">{t('game.stages.0.end')}</div>
      </PopupMessage>
      <PopupMessage
        popupId="end1"
        onClose={toNextStage}
      >
        <div className="whitespace-pre">{t('game.stages.1.end')}</div>
      </PopupMessage>
      <PopupMessage
        popupId="end2"
        onClose={onGameFinished}
      >
        <div className="whitespace-pre">{t('game.stages.2.end')}</div>
      </PopupMessage>

      <PopupEnding />

      <video
        ref={refEnding}
        className="full pointer-events-none hidden object-cover"
        src="assets/videos/ending.mp4"
        playsInline
        onEnded={onEndingEnded}
      />
    </section>
  );
});
